import { Component, Input, Output, EventEmitter, OnInit, SimpleChanges } from '@angular/core';
import { EventResponse, CompanyResponse, EventClassResponse, EventServiceResponse, EventContactResponse, EventSponsorResponse } from '../../../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { TrainingService } from '../../../../../../../../@shared/services/training.service';
import { WizardStep, EventCategory } from '../../create-event-wizard';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-wizard-step-review',
  standalone: false,
  templateUrl: './wizard-step-review.html',
  styleUrl: './wizard-step-review.css'
})
export class WizardStepReview implements OnInit {
  @Input() event: EventResponse | null = null;
  @Input() company: CompanyResponse | null = null;
  @Input() steps: WizardStep[] = [];
  @Input() category: EventCategory | null = null;
  @Output() goToStep = new EventEmitter<number>();

  classCount = 0;
  serviceCount = 0;
  contactCount = 0;
  sponsorCount = 0;
  scheduleCount = 0;
  capacityCount = 0;
  packageCount = 0;
  loading = true;

  // Payment validation
  hasPricedClasses = false;
  hasPricedServices = false;
  hasPricedPackages = false;

  constructor(
    private eventService: EventsService,
    private trainingService: TrainingService
  ) {}

  ngOnInit() {
    this.loadCounts();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['event'] && this.event?.event_id) {
      this.loadCounts();
    }
  }

  private pendingLoads = 0;

  private startLoad() { this.pendingLoads++; }

  private endLoad() {
    this.pendingLoads--;
    if (this.pendingLoads === 0) {
      this.loading = false;
    }
  }

  loadCounts() {
    if (!this.event?.event_id) {
      this.loading = false;
      return;
    }

    const eventId = this.event.event_id;
    const companyId = this.company?.company_id;
    this.pendingLoads = 0;

    // Common load (services, contacts, sponsors) - always runs
    this.startLoad();
    forkJoin({
      services: this.eventService.getEventServices({ event_id: eventId }).pipe(catchError(() => of([]))),
      contacts: this.eventService.getEventContacts({ event_id: eventId }, companyId).pipe(catchError(() => of([]))),
      sponsors: this.eventService.getEventSponsors({ event_id: eventId }, companyId).pipe(catchError(() => of([])))
    }).subscribe({
      next: (results) => {
        this.serviceCount = results.services?.length || 0;
        this.contactCount = results.contacts?.length || 0;
        this.sponsorCount = results.sponsors?.length || 0;
        this.hasPricedServices = results.services?.some(s => s.price && s.price > 0) || false;
        this.endLoad();
      },
      error: () => this.endLoad()
    });

    // Category-specific loads
    if (this.category === 'competition') {
      this.startLoad();
      this.loadCompetitionCounts(eventId);
    } else if (this.category === 'training') {
      this.startLoad();
      this.loadTrainingCounts(eventId);
    }
  }

  loadCompetitionCounts(eventId: number) {
    this.eventService.getEventClasses({ event_id: eventId }).subscribe({
      next: (classes) => {
        this.classCount = classes?.length || 0;
        this.hasPricedClasses = classes?.some(c => c.price && c.price > 0) || false;
        if (classes && classes.length > 0) {
          this.loadScheduleCounts(classes);
        } else {
          this.endLoad();
        }
      },
      error: () => this.endLoad()
    });
  }

  loadTrainingCounts(eventId: number) {
    forkJoin({
      capacity: this.trainingService.getDayCapacity({ event_id: eventId }).pipe(catchError(() => of([]))),
      packages: this.trainingService.getPackages({ event_id: eventId }).pipe(catchError(() => of([])))
    }).subscribe({
      next: (results) => {
        this.capacityCount = results.capacity?.filter(c =>
          c.max_group_sessions > 0 || c.max_individual_sessions > 0
        ).length || 0;
        this.packageCount = results.packages?.length || 0;
        this.hasPricedPackages = results.packages?.some(p => p.price_cents && p.price_cents > 0) || false;
        this.endLoad();
      },
      error: () => this.endLoad()
    });
  }

  loadScheduleCounts(classes: EventClassResponse[]) {
    let pending = classes.length;
    let totalSchedules = 0;

    for (const eventClass of classes) {
      this.eventService.getSchedule({ event_class_id: eventClass.event_class_id }).subscribe({
        next: (schedules) => {
          totalSchedules += (schedules && Array.isArray(schedules)) ? schedules.length : 0;
          pending--;
          if (pending === 0) {
            this.scheduleCount = totalSchedules;
            this.endLoad();
          }
        },
        error: () => {
          pending--;
          if (pending === 0) {
            this.scheduleCount = totalSchedules;
            this.endLoad();
          }
        }
      });
    }
  }

  get isPaymentEnabled(): boolean {
    return this.event?.payment_enabled || false;
  }

  get paymentTiming(): string {
    return this.event?.payment_timing === 'immediate' ? 'Immediate' : 'Pay Later';
  }

  get hasPaymentSetup(): boolean {
    // Check if company has Stripe connected or custom payment enabled
    const company = this.company;
    if (!company) return false;
    return company.stripe_account_status === 'active' || company.custom_payment_enabled === true;
  }

  get hasPricedItems(): boolean {
    return this.hasPricedClasses || this.hasPricedServices || this.hasPricedPackages;
  }

  get hasPaymentIssue(): boolean {
    // Returns true if there are priced items but:
    // 1. No payment setup at company level, OR
    // 2. Payment is disabled for this event
    if (!this.hasPricedItems) return false;

    // If no payment setup at company level
    if (!this.hasPaymentSetup) return true;

    // If payment setup exists but is disabled for this event
    if (!this.event?.payment_enabled) return true;

    return false;
  }

  get isReadyToPublish(): boolean {
    if (!this.event) return false;

    // Check for payment issues first (priced items without payment setup)
    if (this.hasPaymentIssue) return false;

    // Different requirements based on category
    if (this.category === 'competition') {
      // Competition needs at least one class
      return this.classCount > 0;
    } else if (this.category === 'training') {
      // Training needs at least capacity and one package
      return this.capacityCount > 0 && this.packageCount > 0;
    } else {
      // Social events just need basic info (already filled)
      return true;
    }
  }

  get publishRequirementMessage(): string {
    // Payment issue takes priority
    if (this.hasPaymentIssue) {
      if (!this.hasPaymentSetup) {
        return 'You have priced items but no payment method configured. Please either set up payments in Company Settings > Billing, or mark all items as free.';
      }
      if (!this.event?.payment_enabled) {
        return 'You have priced items but payment is disabled for this event. Please either enable payment in the Payment step, or set all item prices to 0.';
      }
    }

    if (this.category === 'competition') {
      if (this.classCount === 0) {
        return 'Please add at least one class before publishing your event.';
      }
    } else if (this.category === 'training') {
      if (this.capacityCount === 0) {
        return 'Please set up daily capacity before publishing your event.';
      }
      if (this.packageCount === 0) {
        return 'Please add at least one package before publishing your event.';
      }
    }
    return '';
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('nb-NO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  navigateToStep(stepId: string) {
    const index = this.steps.findIndex(s => s.id === stepId);
    if (index !== -1) {
      this.goToStep.emit(index);
    }
  }
}
